import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarRange, Check, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const STAT_LABELS = {
  commitments_promised: "Promised",
  commitments_completed: "Completed",
  commitments_blocked: "Blocked",
  commitments_slipped: "Slipped",
  moves_accepted: "Moves accepted",
  moves_modified: "Moves modified",
  moves_rejected: "Moves rejected",
  decisions_made: "Decisions made",
  decisions_due: "Decisions due",
  risks_changed: "Risks changed",
};

export default function Review() {
  const [review, setReview] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: cur }, { data: hist }] = await Promise.all([
        api.get("/coo/reviews/current"),
        api.get("/coo/reviews"),
      ]);
      setReview(cur);
      setHistory(hist);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const acknowledge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/coo/reviews/${review.id}/acknowledge`, {});
      setReview(data);
      toast.success("Review acknowledged.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/coo/reviews/${review.id}/regenerate`, {});
      setReview(data);
      load();
      toast.success(`Regenerated (v${data.version}).`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (loading) return <div className="p-8 wr-label" data-testid="review-loading">Assembling weekly review…</div>;
  if (!review) return <div className="p-8 text-wr-sub" data-testid="review-empty">No review.</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-6 sm:py-8 pb-28" data-testid="review-page">
      <p className="wr-label !text-wr-red">▸ SARGE · WEEKLY REVIEW</p>
      <div className="flex items-center justify-between gap-3 flex-wrap mt-2">
        <h1 className="wr-heading font-bold text-white text-2xl sm:text-3xl flex items-center gap-2" data-testid="review-week">
          <CalendarRange size={24} /> {review.iso_year} · Week {review.iso_week}
          {review.version > 1 && <span className="wr-mono text-xs text-wr-muted">v{review.version}</span>}
        </h1>
        <div className="flex gap-2">
          <Button onClick={regenerate} disabled={busy} variant="outline" data-testid="review-regenerate" className="rounded-none border-wr-border bg-transparent text-wr-sub hover:text-white wr-mono uppercase text-xs h-9">
            <RefreshCw size={13} className="mr-1.5" /> Regenerate
          </Button>
          {!review.acknowledged ? (
            <Button onClick={acknowledge} disabled={busy} data-testid="review-acknowledge" className="rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase text-xs h-9">
              <Check size={13} className="mr-1.5" /> Acknowledge
            </Button>
          ) : (
            <span className="wr-mono text-[0.6rem] uppercase px-2.5 py-2 border border-emerald-500/40 text-emerald-400" data-testid="review-acknowledged">Acknowledged</span>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-px bg-wr-border border border-wr-border" data-testid="review-stats">
        {Object.entries(STAT_LABELS).map(([k, label]) => (
          <div key={k} className="bg-wr-surface p-3" data-testid={`review-stat-${k}`}>
            <p className="wr-mono text-lg text-white">{review.stats?.[k] ?? 0}</p>
            <p className="wr-label !text-[0.55rem] mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      <p className="text-xs text-wr-muted mt-3">{review.financial_summary}</p>

      {review.error && <p className="text-sm text-amber-400 mt-4" data-testid="review-error">{review.error}</p>}
      {review.learnings && (
        <div className="mt-5 border-l-2 border-wr-border pl-4" data-testid="review-learnings">
          <p className="wr-label">What Sarge learned</p>
          <p className="text-sm text-wr-sub mt-1 leading-relaxed whitespace-pre-wrap">{review.learnings}</p>
        </div>
      )}
      {review.proposed_focus && (
        <div className="mt-5 border border-wr-red/30 bg-wr-redMuted p-4" data-testid="review-focus">
          <p className="wr-label !text-wr-red">Proposed focus — next week</p>
          <p className="text-sm text-white mt-1 leading-relaxed whitespace-pre-wrap">{review.proposed_focus}</p>
        </div>
      )}

      {history.length > 1 && (
        <div className="mt-8" data-testid="review-history">
          <p className="wr-label">Past reviews</p>
          <div className="mt-2 space-y-1">
            {history.map((h) => (
              <div key={h.id} className="flex items-center gap-3 border-b border-wr-border py-1.5 text-xs" data-testid={`review-hist-${h.id}`}>
                <span className="text-white">{h.iso_year} · W{h.iso_week}</span>
                <span className="wr-mono text-[0.6rem] text-wr-muted">promised {h.stats?.commitments_promised ?? 0} · done {h.stats?.commitments_completed ?? 0}</span>
                {h.acknowledged && <span className="wr-mono text-[0.55rem] text-emerald-400 ml-auto">ack</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
